import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, Globe2, Info } from "lucide-react";
import TimeConverter from "../components/timeConverter/TimeConverter";
import { browserTimeZone, parseShareParams, IST_TZ, PUBLIC_CONVERTER_URL } from "../lib/timeConverter";

const STEPS = [
    "Choose the direction: your local time → India (IST), or India (IST) → your local time.",
    "Search your city, country or time zone (e.g. Mexico City, Dubai, London, Sydney).",
    "Pick the date and time — the converted date and time appear instantly, with daylight saving applied.",
];

export default function TimeConverterPage() {
    const { search } = useLocation();
    const { values, rejected } = useMemo(() => parseShareParams(search), [search]);
    const local = browserTimeZone();
    const defaultTz = local && local !== IST_TZ && local !== "Asia/Calcutta" ? local : "";

    return (
        <div className="bg-[#F8FAFC] min-h-screen" data-testid="time-converter-page">
            <Helmet>
                <title>International Time Converter — Local Time to IST | Anand Tutorials</title>
                <meta name="description" content="Free international time converter by Anand Tutorials. Convert your local time to India Standard Time (IST) and back, with daylight saving handled automatically — plan your online demo class timing." />
                <link rel="canonical" href={PUBLIC_CONVERTER_URL} />
            </Helmet>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-6">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-600 inline-flex items-center gap-1.5">
                    <Globe2 className="w-3.5 h-3.5" /> Anand Tutorials · Worldwide
                </div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-2">International Time Converter</h1>
                <p className="text-sm sm:text-base text-slate-600 mt-3 max-w-2xl">
                    Check your preferred online class timing in your local time zone and its equivalent time in India (IST) — or the other way round.
                </p>
                {values.tz && (
                    <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#1E3A8A] bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-full" data-testid="tc-public-prefilled">
                        <Clock className="w-3.5 h-3.5" /> A timing was shared with you — change any field to compare other times.
                    </div>
                )}
                {rejected && (
                    <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg" data-testid="tc-public-invalid-link">
                        Some values in this link were not valid and have been ignored. Please choose them below.
                    </div>
                )}
            </section>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
                <TimeConverter variant="public" initial={values} defaultTz={defaultTz} />
            </section>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14">
                <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
                    <h2 className="font-heading font-bold text-base md:text-lg text-[#0F172A] inline-flex items-center gap-2">
                        <Info className="w-4 h-4 text-[#1E3A8A]" /> How to use
                    </h2>
                    <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal pl-5">
                        {STEPS.map((s) => <li key={s}>{s}</li>)}
                    </ol>
                    <p className="text-xs text-slate-500 mt-4">
                        The converter calculates times only. A demo class is confirmed only after the Anand Tutorials team checks tutor availability and confirms with you.
                    </p>
                </div>
            </section>
        </div>
    );
}
